import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Pagination,
  IconButton,
  Stack,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Description as CsvIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Email as EmailIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import { CampaignFilterService } from '../services/campaignFilterService';
import { exportEmployeeTimeSheetToCSV, exportEmployeeTimeSheetToExcel, exportEmployeeTimeSheetToPDF } from '../utils/exportUtils';
import ShareReportDialog from '../components/ShareReportDialog';
import SearchableSelect from '../components/SearchableSelect';
import { filterUsersVisibleToViewer, isSuperUserRole } from '../utils/userVisibilityPolicy';
import ReportColumnPicker from '../components/reports/ReportColumnPicker';
import {
  EMPLOYEE_TIME_SHEET_COLUMNS,
  EMPLOYEE_TIME_SHEET_REPORT_KEY,
  getEmployeeTimeSheetColumnDefs,
  type EmployeeTimeSheetColumnDef,
  type EmployeeTimeSheetColumnKey,
} from '../constants/employeeTimeSheetColumns';
import {
  getReportVisibleColumns,
  resetReportVisibleColumns,
  saveReportVisibleColumns,
} from '../utils/reportColumnPreferences';
import moment from 'moment-timezone';
import { Campaign, Department, User } from '../types';
import { Link } from 'react-router-dom';
import { TRAINING_CHIP_STYLE } from '../utils/attendanceLabels';

interface TimeSheetRow {
  userId: string;
  employeeId: string;
  employeeName: string;
  hireDate?: string | null;
  hiredOnOrBeforeMonthStart?: boolean;
  assignmentStatus?: 'unassigned' | 'scheduled' | 'absent' | string;
  onTrainingInPeriod?: boolean;
  daysInTraining?: number;
  trainingStartedOn?: string | null;
  trainingBonusCategory?: 'training' | 'disqualified' | null;
  trainingBonusReason?: string | null;
  department: string;
  totalWorkedHours: number;
  absenceDays: number;
  absenceHours: number;
  tardyStartMinutes: number;
  tardyBreakMinutes: number;
  tardyLunchMinutes: number;
  leavingEarlyMinutes: number;
  manualAdjustmentCount?: number;
  manualAdjustmentPendingHighImpact?: number;
  manualAdjustmentBreakdown?: { adds: number; edits: number; deletes: number };
  verbalWarnings?: number;
  writtenWarnings?: number;
  finalWarnings?: number;
  totalWarnings?: number;
  details?: Array<{
    date: string;
    workedHours: number;
    absenceHours: number;
    absenceTypes?: string[];
    tardyStartMinutes: number;
    tardyBreakMinutes: number;
    tardyLunchMinutes: number;
    leavingEarlyMinutes: number;
    manualAdjustmentCount?: number;
    manualAdjustmentPendingHighImpact?: number;
  }>;
}

interface ReportFilters {
  startDate: string;
  endDate: string;
  userId: string;
  departmentId: string;
  campaignId: string;
  page: number;
  limit: number;
}

interface ReportResponse {
  success: boolean;
  data: TimeSheetRow[];
  warningMessage?: string;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

function calendarQuery(params: URLSearchParams, name: string): string {
  const value = String(params.get(name) || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '';
}

function normalizeEmployeeNumber(value: string | null | undefined): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.replace(/^0+/, '') || '0';
}

/** Dates and employee from a bonus proof link: ?startDate=&endDate=&employeeNumber=&userId= */
function timeSheetFiltersFromLocation(): ReportFilters {
  const params = new URLSearchParams(window.location.search);
  const startDate = calendarQuery(params, 'startDate');
  const endDate = calendarQuery(params, 'endDate');
  return {
    startDate: startDate || moment().subtract(2, 'weeks').format('YYYY-MM-DD'),
    endDate: endDate || moment().format('YYYY-MM-DD'),
    userId: String(params.get('userId') || '').trim(),
    departmentId: '',
    campaignId: '',
    page: 1,
    limit: 25,
  };
}

const EmployeeTimeSheetReportPage: React.FC = () => {
  const { user } = useAuth();
  const linkedEmployeeNumber = new URLSearchParams(window.location.search).get('employeeNumber');
  const [filters, setFilters] = useState<ReportFilters>(timeSheetFiltersFromLocation);
  const [showFilters, setShowFilters] = useState(true);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareReportData, setShareReportData] = useState<TimeSheetRow[] | null>(null);
  const [isPreparingShare, setIsPreparingShare] = useState(false);
  const [sortBy, setSortBy] = useState<'employee' | 'worked' | 'absenceDays' | 'absenceHours' | 'tardyStart' | 'tardyBreak' | 'tardyLunch' | 'leavingEarly' | 'daysInTraining'>('employee');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [visibleColumns, setVisibleColumns] = useState<EmployeeTimeSheetColumnKey[]>(() =>
    getReportVisibleColumns(EMPLOYEE_TIME_SHEET_REPORT_KEY, user?.id) as EmployeeTimeSheetColumnKey[],
  );

  React.useEffect(() => {
    if (user?.id) {
      setVisibleColumns(
        getReportVisibleColumns(EMPLOYEE_TIME_SHEET_REPORT_KEY, user.id) as EmployeeTimeSheetColumnKey[],
      );
    }
  }, [user?.id]);

  const visibleColumnDefs = React.useMemo(
    () => getEmployeeTimeSheetColumnDefs(visibleColumns),
    [visibleColumns],
  );

  const exportFilters = React.useMemo(
    () => ({ ...filters, visibleColumns }),
    [filters, visibleColumns],
  );

  const canShareReports = ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || '');
  const isAgent = user?.role === 'agent';

  const { data: departmentsData } = useQuery<{ data: { success: boolean; data: Department[] } }, Error>({
    queryKey: ['departments'],
    queryFn: apiClient.departments.getAll,
    enabled: ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  const { data: campaignsData } = useQuery<{ data: { success: boolean; data: Campaign[] } }, Error>({
    queryKey: ['campaigns', filters.startDate, filters.endDate],
    queryFn: () => apiClient.campaigns.getAll({
      includeWithActivityFrom: filters.startDate,
      includeWithActivityTo: filters.endDate,
    }),
    enabled: ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => apiClient.users.getAll(),
    enabled: ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // A bonus link names the employee by number. Select that person once the directory loads.
  useEffect(() => {
    if (!linkedEmployeeNumber || filters.userId) return;
    const users = usersData?.data?.data || [];
    if (!users.length) return;
    const target = normalizeEmployeeNumber(linkedEmployeeNumber);
    const match = users.find((person: User) => normalizeEmployeeNumber(person.employeeId) === target);
    if (!match?.id) return;
    setFilters((prev) => (prev.userId ? prev : { ...prev, userId: match.id, page: 1 }));
  }, [usersData, linkedEmployeeNumber, filters.userId]);

  const availableDepartments = departmentsData?.data.data
    ? (user && ['team_lead', 'supervisor'].includes(user.role)
      ? departmentsData.data.data.filter(dept => dept.id === user.departmentId)
      : departmentsData.data.data)
    : [];

  const availableCampaigns = campaignsData?.data.data
    ? (user ? CampaignFilterService.getUserAccessibleCampaigns(user, campaignsData.data.data) : [])
    : [];

  const { data: reportResponse, isLoading, error, refetch } = useQuery<ReportResponse>({
    queryKey: ['employeeTimeSheetReport', filters],
    queryFn: async () => {
      const response = await apiClient.timeEntries.getEmployeeTimeSheet(filters);
      return response.data;
    },
    enabled: true,
  });

  useEffect(() => {
    if (!filters.userId) return;
    document.querySelector('[data-timesheet-focus="true"]')?.scrollIntoView({ block: 'center' });
  }, [reportResponse, filters.userId]);

  const handleFilterChange = (field: keyof ReportFilters, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: field !== 'page' ? 1 : (typeof value === 'number' ? value : prev.page),
    }));
  };

  const handleSearch = () => {
    refetch();
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: moment().subtract(2, 'weeks').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      userId: '',
      departmentId: '',
      campaignId: '',
      page: 1,
      limit: 25,
    });
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    handleFilterChange('page', value);
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const getAssignmentStatusLabel = (status?: string) => {
    switch (status) {
      case 'absent':
        return 'Absent';
      case 'scheduled':
        return 'Scheduled';
      case 'unassigned':
        return 'Unassigned';
      default:
        return status || 'Unassigned';
    }
  };

  const getAssignmentStatusColor = (status?: string) => {
    switch (status) {
      case 'absent':
        return 'info';
      case 'scheduled':
        return 'success';
      case 'unassigned':
      default:
        return 'warning';
    }
  };

  const fetchAllForExport = async () => {
    const baseParams = {
      ...filters,
      page: 1,
      limit: 1000,
    };
    let allRecords: TimeSheetRow[] = [];
    let page = 1;
    let total = 0;

    while (true) {
      const response = await apiClient.timeEntries.getEmployeeTimeSheet({
        ...baseParams,
        page
      });
      const pageData = response.data.data || [];
      const pagination = response.data.pagination;
      allRecords = allRecords.concat(pageData);
      total = pagination?.total ?? allRecords.length;
      if (allRecords.length >= total || pageData.length === 0) {
        break;
      }
      page += 1;
    }

    return allRecords;
  };

  const getSortedRows = React.useCallback((rows: TimeSheetRow[]) => {
    const list = rows ? [...rows] : [];
    list.sort((a, b) => {
      let av: any = '';
      let bv: any = '';
      switch (sortBy) {
        case 'worked':
          av = a.totalWorkedHours || 0;
          bv = b.totalWorkedHours || 0;
          break;
        case 'absenceDays':
          av = a.absenceDays || 0;
          bv = b.absenceDays || 0;
          break;
        case 'absenceHours':
          av = a.absenceHours || 0;
          bv = b.absenceHours || 0;
          break;
        case 'tardyStart':
          av = a.tardyStartMinutes || 0;
          bv = b.tardyStartMinutes || 0;
          break;
        case 'tardyBreak':
          av = a.tardyBreakMinutes || 0;
          bv = b.tardyBreakMinutes || 0;
          break;
        case 'tardyLunch':
          av = a.tardyLunchMinutes || 0;
          bv = b.tardyLunchMinutes || 0;
          break;
        case 'leavingEarly':
          av = a.leavingEarlyMinutes || 0;
          bv = b.leavingEarlyMinutes || 0;
          break;
        case 'daysInTraining':
          av = a.daysInTraining || 0;
          bv = b.daysInTraining || 0;
          break;
        case 'employee':
        default:
          av = (a.employeeName || '').toLowerCase();
          bv = (b.employeeName || '').toLowerCase();
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [sortBy, sortDir]);

  const handleExportCSV = async () => {
    try {
      const data = getSortedRows(await fetchAllForExport());
      exportEmployeeTimeSheetToCSV(data, exportFilters);
    } catch (error) {
      console.error('Error exporting employee time sheet CSV:', error);
      alert('Failed to export CSV. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const handleExportExcel = async () => {
    try {
      const data = getSortedRows(await fetchAllForExport());
      exportEmployeeTimeSheetToExcel(data, exportFilters);
    } catch (error) {
      console.error('Error exporting employee time sheet Excel:', error);
      alert('Failed to export Excel. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const handleExportPDF = async () => {
    try {
      const data = getSortedRows(await fetchAllForExport());
      exportEmployeeTimeSheetToPDF(data, exportFilters);
    } catch (error) {
      console.error('Error exporting employee time sheet PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const formatMinutes = (minutes: number) => {
    const totalMinutes = Math.round(minutes || 0);
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return `${hrs}h ${mins}m`;
  };

  const toggleSort = (col: typeof sortBy) => {
    if (sortBy === col) setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  const toggleRow = (userId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const formatAbsenceTypes = (types?: string[]) => {
    if (!types || types.length === 0) return 'None';
    return types.join(', ');
  };

  const renderIncidentCell = (value: number, unit: 'hours' | 'minutes') => {
    const isIncident = value > 0;
    const displayValue = unit === 'minutes' ? formatMinutes(value) : value;
    return isIncident ? (
      <Chip label={displayValue} size="small" color="warning" />
    ) : (
      <Typography variant="body2">{displayValue}</Typography>
    );
  };

  const warningsDateRangeLabel = `Warnings for ${filters.startDate || 'N/A'} to ${filters.endDate || 'N/A'}`;

  const renderAbsenceTypesCell = (types?: string[]) => {
    const label = formatAbsenceTypes(types);
    return types && types.length > 0 ? (
      <Chip label={label} size="small" color="error" />
    ) : (
      <Typography variant="body2">{label}</Typography>
    );
  };

  const sortedRows = React.useMemo(() => {
    const list = reportResponse?.data ? [...reportResponse.data] : [];
    return getSortedRows(list);
  }, [reportResponse?.data, getSortedRows]);

  const handleShareOpen = async () => {
    try {
      setIsPreparingShare(true);
      const data = getSortedRows(await fetchAllForExport());
      setShareReportData(data);
      setShareDialogOpen(true);
    } catch (error) {
      console.error('Error preparing employee time sheet share:', error);
      alert('Failed to prepare report for sharing. Please try again.');
    } finally {
      setIsPreparingShare(false);
    }
  };

  const handleVisibleColumnsChange = (columns: string[]) => {
    const ordered = EMPLOYEE_TIME_SHEET_COLUMNS
      .map((column) => column.key)
      .filter((key) => columns.includes(key));
    const next = saveReportVisibleColumns(EMPLOYEE_TIME_SHEET_REPORT_KEY, user?.id, ordered) as EmployeeTimeSheetColumnKey[];
    setVisibleColumns(next);
  };

  const handleResetVisibleColumns = () => {
    const next = resetReportVisibleColumns(EMPLOYEE_TIME_SHEET_REPORT_KEY, user?.id) as EmployeeTimeSheetColumnKey[];
    setVisibleColumns(next);
  };

  const renderColumnCell = (column: EmployeeTimeSheetColumnDef, row: TimeSheetRow) => {
    switch (column.key) {
      case 'employee':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <IconButton size="small" onClick={() => toggleRow(row.userId)} sx={{ p: 0.25, flexShrink: 0 }}>
              {expandedRows.has(row.userId) ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.78rem', lineHeight: 1.2 }}>
                {row.employeeName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.65rem', display: 'block' }}>
                {row.employeeId}
              </Typography>
            </Box>
          </Box>
        );
      case 'hireDate': {
        const hireLabel = row.hireDate || 'N/A';
        if (!row.hireDate) {
          return <Typography variant="caption" noWrap sx={{ fontSize: '0.72rem' }}>{hireLabel}</Typography>;
        }
        const hireTip = row.hiredOnOrBeforeMonthStart
          ? `Hired on or before the 1st of the period month (bonus start rule met)`
          : `Hired after the 1st of the period month (bonus start rule not met for mid-month hires)`;
        return (
          <Tooltip title={hireTip} arrow>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25, minWidth: 0 }}>
              <Typography variant="caption" noWrap sx={{ fontSize: '0.72rem' }}>{hireLabel}</Typography>
              {row.onTrainingInPeriod && (
                <Chip
                  size="small"
                  label={row.hiredOnOrBeforeMonthStart ? '≤ 1st' : 'After 1st'}
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    bgcolor: row.hiredOnOrBeforeMonthStart ? 'rgba(46, 125, 50, 0.12)' : 'rgba(239, 108, 0, 0.12)',
                    color: row.hiredOnOrBeforeMonthStart ? '#2e7d32' : '#ef6c00',
                    '& .MuiChip-label': { px: 0.5 },
                  }}
                />
              )}
            </Box>
          </Tooltip>
        );
      }
      case 'status': {
        const days = Number(row.daysInTraining) || 0;
        const trainingLabel = days === 1 ? 'On Training · 1 day' : `On Training · ${days} days`;
        const trainingTip = [
          row.trainingStartedOn ? `Training started ${row.trainingStartedOn}` : null,
          row.trainingBonusReason || null,
          row.trainingBonusCategory === 'disqualified'
            ? 'Bonus rule: disqualified for this month’s training window'
            : row.trainingBonusCategory === 'training'
              ? 'Bonus rule: counts as Training for this month'
              : null,
        ].filter(Boolean).join(' · ');
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.35, minWidth: 0 }}>
            <Chip
              label={getAssignmentStatusLabel(row.assignmentStatus)}
              color={getAssignmentStatusColor(row.assignmentStatus)}
              size="small"
              sx={{ height: 20, fontSize: '0.65rem', maxWidth: '100%', '& .MuiChip-label': { px: 0.75 } }}
            />
            {row.onTrainingInPeriod && (
              <Tooltip title={trainingTip || 'Scheduled on Training campaign in this period'} arrow>
                <Chip
                  label={trainingLabel}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.62rem',
                    fontWeight: 600,
                    maxWidth: '100%',
                    bgcolor: TRAINING_CHIP_STYLE.bgcolor,
                    color: TRAINING_CHIP_STYLE.color,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              </Tooltip>
            )}
          </Box>
        );
      }
      case 'daysInTraining':
        return (
          <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>
            {row.daysInTraining ?? 0}
          </Typography>
        );
      case 'workedHours':
        return row.totalWorkedHours;
      case 'absenceDays':
        return row.absenceDays;
      case 'absenceHours':
        return row.absenceHours;
      case 'tardyStart':
        return <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>{formatMinutes(row.tardyStartMinutes)}</Typography>;
      case 'tardyBreak':
        return <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>{formatMinutes(row.tardyBreakMinutes)}</Typography>;
      case 'tardyLunch':
        return <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>{formatMinutes(row.tardyLunchMinutes)}</Typography>;
      case 'leavingEarly':
        return <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>{formatMinutes(row.leavingEarlyMinutes)}</Typography>;
      case 'manualAdj': {
        const count = row.manualAdjustmentCount || 0;
        if (count <= 0) return <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>0</Typography>;
        const pending = row.manualAdjustmentPendingHighImpact || 0;
        const breakdown = row.manualAdjustmentBreakdown;
        const tip = [
          breakdown ? `${breakdown.adds} Add · ${breakdown.edits} Edit · ${breakdown.deletes} Delete` : `${count} manual adjustment event(s)`,
          pending > 0 ? `${pending} Pending High-Impact HR Review` : null,
        ].filter(Boolean).join(' · ');
        return (
          <Tooltip title={tip}>
            <Chip
              size="small"
              color={pending > 0 ? 'error' : 'warning'}
              label={`⚠ ${count}`}
              component={Link}
              to={`/reports/manual-time-adjustments?employeeId=${encodeURIComponent(row.userId)}&startDate=${encodeURIComponent(filters.startDate)}&endDate=${encodeURIComponent(filters.endDate)}`}
              clickable
              onClick={(e) => e.stopPropagation()}
              sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, textDecoration: 'none' }}
            />
          </Tooltip>
        );
      }
      case 'verbalWarnings':
        return (
          <Tooltip title={warningsDateRangeLabel}>
            <span>{row.verbalWarnings ?? 0}</span>
          </Tooltip>
        );
      case 'writtenWarnings':
        return (
          <Tooltip title={warningsDateRangeLabel}>
            <span>{row.writtenWarnings ?? 0}</span>
          </Tooltip>
        );
      case 'finalWarnings':
        return (
          <Tooltip title={warningsDateRangeLabel}>
            <span>{row.finalWarnings ?? 0}</span>
          </Tooltip>
        );
      case 'totalWarnings':
        return (
          <Tooltip title={warningsDateRangeLabel}>
            <span>{row.totalWarnings ?? 0}</span>
          </Tooltip>
        );
      default:
        return null;
    }
  };

  const activeFilters = Object.entries(filters).filter(([key, value]) =>
    value !== '' && value !== 0 && key !== 'page' && key !== 'limit'
  );

  const sortIndicator = (col: typeof sortBy) => (sortBy === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const headerCellSx = {
    px: 0.75,
    py: 1,
    fontSize: '0.72rem',
    fontWeight: 700,
    whiteSpace: 'nowrap' as const,
    lineHeight: 1.2,
    cursor: 'pointer',
  };

  const bodyCellSx = {
    px: 0.75,
    py: 0.75,
    fontSize: '0.78rem',
    verticalAlign: 'middle' as const,
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: { xs: 1.5, sm: 2, md: 3 } }}>
      <Typography variant="h4" gutterBottom>
        Employee Time Sheet
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Filters</Typography>
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterIcon />
            </IconButton>
          </Box>

          {showFilters && (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="End Date"
                  type="date"
                  fullWidth
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {!isAgent && (
                <Grid item xs={12} sm={6} md={3}>
                  <SearchableSelect
                    label="Department"
                    value={filters.departmentId}
                    onChange={(value) => handleFilterChange('departmentId', String(value))}
                    options={availableDepartments.map((d: Department) => ({
                      value: d.id,
                      label: d.name,
                    }))}
                  />
                </Grid>
              )}

              {!isAgent && (
                <Grid item xs={12} sm={6} md={3}>
                  <SearchableSelect
                    label="Campaign"
                    value={filters.campaignId}
                    onChange={(value) => handleFilterChange('campaignId', String(value))}
                    options={availableCampaigns.map((c: Campaign) => ({
                      value: c.id,
                      label: c.name,
                    }))}
                  />
                </Grid>
              )}

              {!isAgent && (
                <Grid item xs={12} sm={6} md={3}>
                  <SearchableSelect
                    label="Employee"
                    value={filters.userId}
                    onChange={(value) => handleFilterChange('userId', String(value))}
                    options={filterUsersVisibleToViewer(usersData?.data?.data || [], user?.role)
                      .filter((emp: User) => !isSuperUserRole(emp.role))
                      .map((emp: User) => ({
                        value: emp.id,
                        label: `${emp.firstName} ${emp.lastName} (${emp.employeeId})`,
                      }))}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Records per page"
                  value={filters.limit}
                  onChange={(value) => handleFilterChange('limit', value as number)}
                  options={[10, 25, 50, 100].map((value) => ({
                    value,
                    label: String(value),
                  }))}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleResetFilters}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    disabled={isLoading}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={handleExportMenuOpen}
                    disabled={!reportResponse?.data || reportResponse.data.length === 0}
                  >
                    Export
                  </Button>
                  {canShareReports && (
                    <Button
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      onClick={handleShareOpen}
                      disabled={!reportResponse?.data || reportResponse.data.length === 0 || isPreparingShare}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {isPreparingShare ? 'Preparing...' : 'Share'}
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>
          )}

          {activeFilters.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Active Filters:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {activeFilters.map(([key, value]) => (
                  <Chip key={key} label={`${key}: ${value}`} size="small" onDelete={() => handleFilterChange(key as keyof ReportFilters, '')} />
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleExportCSV}>
          <ListItemIcon>
            <CsvIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportExcel}>
          <ListItemIcon>
            <ExcelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to Excel</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportPDF}>
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to PDF</ListItemText>
        </MenuItem>
      </Menu>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
            <Typography variant="h6">
              Employee Time Sheet
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({filters.startDate || 'N/A'} to {filters.endDate || 'N/A'})
              </Typography>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReportColumnPicker
                columns={EMPLOYEE_TIME_SHEET_COLUMNS.map((column) => ({
                  key: column.key,
                  label: column.label,
                  required: column.required,
                }))}
                visibleColumns={visibleColumns}
                onChange={handleVisibleColumnsChange}
                onReset={handleResetVisibleColumns}
              />
              {isLoading && <CircularProgress size={24} />}
            </Box>
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">Failed to load report data. Please try again.</Alert>
          ) : reportResponse?.data && reportResponse.data.length > 0 ? (
            <>
              {reportResponse.warningMessage && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {reportResponse.warningMessage}
                </Alert>
              )}
              <TableContainer component={Paper} sx={{ overflowX: 'hidden' }}>
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      {visibleColumnDefs.map((column) => (
                        <TableCell
                          key={column.key}
                          align={column.align || 'left'}
                          sx={{
                            ...headerCellSx,
                            width: `${column.widthPct}%`,
                            cursor: column.sortKey ? 'pointer' : 'default',
                          }}
                          onClick={column.sortKey ? () => toggleSort(column.sortKey!) : undefined}
                        >
                          <Tooltip title={column.tooltip} arrow>
                            <span>
                              {column.shortLabel}
                              {column.sortKey ? sortIndicator(column.sortKey) : ''}
                            </span>
                          </Tooltip>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedRows.map((row) => {
                      const pointed = Boolean(filters.userId)
                        && (row.userId === filters.userId
                          || normalizeEmployeeNumber(row.employeeId) === normalizeEmployeeNumber(linkedEmployeeNumber));
                      return (
                      <React.Fragment key={row.userId}>
                        <TableRow
                          selected={pointed}
                          data-timesheet-focus={pointed ? 'true' : undefined}
                          sx={{
                            backgroundColor: (row.totalWarnings || 0) > 0 ? 'rgba(255, 183, 77, 0.18)' : 'inherit'
                          }}
                        >
                          {visibleColumnDefs.map((column) => (
                            <TableCell key={`${row.userId}-${column.key}`} align={column.align || 'left'} sx={bodyCellSx}>
                              {renderColumnCell(column, row)}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={visibleColumnDefs.length} sx={{ py: 0, borderBottom: 0 }}>
                            <Collapse in={expandedRows.has(row.userId)} timeout="auto" unmountOnExit>
                              <Box sx={{ my: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                  Daily Details
                                </Typography>
                                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={headerCellSx}>Date</TableCell>
                                      <TableCell align="center" sx={headerCellSx}>Worked</TableCell>
                                      <TableCell align="center" sx={headerCellSx}>Abs Hrs</TableCell>
                                      <TableCell sx={headerCellSx}>Abs Types</TableCell>
                                      <TableCell align="center" sx={headerCellSx}>Tardy In</TableCell>
                                      <TableCell align="center" sx={headerCellSx}>Tardy Brk</TableCell>
                                      <TableCell align="center" sx={headerCellSx}>Tardy Lnc</TableCell>
                                      <TableCell align="center" sx={headerCellSx}>Left Early</TableCell>
                                      <TableCell align="center" sx={headerCellSx}>Manual Adj.</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(row.details || []).map((detail, detailIndex) => (
                                      <TableRow
                                        key={`${row.userId}-${detail.date}`}
                                        sx={{
                                          backgroundColor: detailIndex % 2 === 1 ? 'rgba(0, 0, 0, 0.03)' : 'transparent'
                                        }}
                                      >
                                        <TableCell sx={bodyCellSx}>
                                          <Link
                                            to={`/management/time-records?userId=${row.userId}&date=${detail.date}`}
                                            style={{ textDecoration: 'none' }}
                                          >
                                            <Typography variant="caption" color="primary" sx={{ fontSize: '0.72rem' }}>
                                              {detail.date}
                                            </Typography>
                                          </Link>
                                        </TableCell>
                                        <TableCell align="center" sx={bodyCellSx}>{detail.workedHours}</TableCell>
                                        <TableCell align="center" sx={bodyCellSx}>{renderIncidentCell(detail.absenceHours, 'hours')}</TableCell>
                                        <TableCell sx={bodyCellSx}>{renderAbsenceTypesCell(detail.absenceTypes)}</TableCell>
                                        <TableCell align="center" sx={bodyCellSx}>{renderIncidentCell(detail.tardyStartMinutes, 'minutes')}</TableCell>
                                        <TableCell align="center" sx={bodyCellSx}>{renderIncidentCell(detail.tardyBreakMinutes, 'minutes')}</TableCell>
                                        <TableCell align="center" sx={bodyCellSx}>{renderIncidentCell(detail.tardyLunchMinutes, 'minutes')}</TableCell>
                                        <TableCell align="center" sx={bodyCellSx}>{renderIncidentCell(detail.leavingEarlyMinutes, 'minutes')}</TableCell>
                                        <TableCell align="center" sx={bodyCellSx}>
                                          {(detail.manualAdjustmentCount || 0) > 0 ? (
                                            <Chip
                                              size="small"
                                              color={(detail.manualAdjustmentPendingHighImpact || 0) > 0 ? 'error' : 'warning'}
                                              label={`⚠ ${detail.manualAdjustmentCount}`}
                                              component={Link}
                                              to={`/reports/manual-time-adjustments?employeeId=${encodeURIComponent(row.userId)}&workDate=${encodeURIComponent(detail.date)}`}
                                              clickable
                                              onClick={(e) => e.stopPropagation()}
                                              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, textDecoration: 'none' }}
                                            />
                                          ) : (
                                            <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>0</Typography>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {reportResponse?.pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={reportResponse.pagination.totalPages}
                    page={reportResponse.pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Alert severity="info">No data found for the selected filters.</Alert>
          )}
        </CardContent>
      </Card>

      {canShareReports && (
        <ShareReportDialog
          open={shareDialogOpen}
          onClose={() => {
            setShareDialogOpen(false);
            setShareReportData(null);
          }}
          reportType="employee-time-sheet"
          reportFilters={exportFilters}
          reportData={shareReportData || undefined}
          reportTitle="Employee Time Sheet"
          onShareSuccess={() => {
            console.log('Report shared successfully');
          }}
        />
      )}
    </Container>
  );
};

export default EmployeeTimeSheetReportPage;
